import {
  InputSize,
  Select,
  Option,
  sizedSelectStyles
} from "shared/components";
import { Avatar } from "shared/components/Avatar";
import { cx } from "shared/utils";
import { useCallback } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowDown01Icon,
  UserMinus01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import type { StylesConfig } from "react-select";

import { useAssigneeSelect } from "./hooks";
import type { AssigneeOption } from "./types";

interface AssigneeSelectProps {
  searchable?: boolean;
  value?: number | null;
  rowId?: string;
  propertyId?: number;
  propertyIds?: number[];
  inputClassName?: string;
  labelClassName?: string;
  onChange?: (partner_user_id: number | null) => void;
  onAssign?: (rowId: string, userId: number | null) => Promise<unknown>;
  onAssignSuccess?: () => void;
  label?: string;
  size?: InputSize;
  /** 32px tall with the icon library's arrow, for the application top bar; the design system's smallest select is 40px. */
  compact?: boolean;
  successMessage?: string;
  errorMessage?: string;
}

const selectStyles = {
  valueContainer: (base) => ({
    ...base,
    alignItems: "center",
    paddingTop: 0,
    paddingBottom: 0
  }),
  singleValue: (base) => ({
    ...base,
    margin: 0,
    maxWidth: "100%",
    display: "flex",
    alignItems: "center"
  })
};

const COMPACT_HEIGHT = 32;

const compactSelectStyles: StylesConfig = {
  ...selectStyles,
  control: (base, state) => ({
    ...sizedSelectStyles(InputSize.SM).control?.(base, state),
    minHeight: COMPACT_HEIGHT,
    height: COMPACT_HEIGHT,
    maxHeight: COMPACT_HEIGHT
  })
};

const CompactDropdownIndicator = () => (
  <HugeiconsIcon
    icon={ArrowDown01Icon}
    size={16}
    className="mr-2 shrink-0 text-content-1000"
  />
);

const renderOptionLabel = (
  option: Option,
  meta: { context: string },
  currentUserId?: number,
  compact?: boolean
) => {
  const assigneeOption = option as AssigneeOption;
  const isValue = meta.context === "value";
  // The top bar's 32px select sets its value in smaller type.
  const textSize = compact && isValue ? "text-sm" : "text-base";
  const initialsSize = compact && isValue ? "text-[8px]" : "text-[9px]";

  if (assigneeOption.kind === "user" && assigneeOption.user)
    return (
      <div
        className={cx("flex items-center gap-2 min-w-0", !isValue && "py-1")}
      >
        <Avatar
          name={assigneeOption.user.name}
          className={cx("font-black", initialsSize)}
          size={20}
        />
        <span
          className={cx(
            "font-medium text-content-1000 truncate min-w-0",
            textSize
          )}
        >
          {assigneeOption.user.name}
        </span>
        {assigneeOption.user.id === currentUserId ? (
          <span
            className={cx(
              "font-medium text-content-200 whitespace-nowrap shrink-0",
              textSize
            )}
          >
            (You)
          </span>
        ) : null}
      </div>
    );

  if (assigneeOption.kind === "unassigned")
    return (
      <div
        className={cx("flex items-center gap-2", isValue ? "min-w-0" : "py-1")}
      >
        <HugeiconsIcon
          icon={UserMinus01Icon}
          className="text-content-200"
          size={20}
        />
        <span
          className={cx(
            "font-medium text-content-200",
            textSize,
            isValue && "block w-full truncate"
          )}
        >
          {assigneeOption.label as string}
        </span>
      </div>
    );

  return (
    <span
      className={cx(
        "text-base font-medium text-content-200",
        isValue ? "block w-full truncate" : "flex items-center gap-2 py-1"
      )}
    >
      {assigneeOption.label as string}
    </span>
  );
};

export const AssigneeSelect = ({
  searchable,
  value,
  rowId,
  propertyId,
  propertyIds,
  inputClassName,
  labelClassName,
  onChange,
  onAssign,
  onAssignSuccess,
  label,
  size = InputSize.SM,
  compact = false,
  successMessage,
  errorMessage
}: AssigneeSelectProps) => {
  const select = useAssigneeSelect({
    value,
    rowId,
    propertyId,
    propertyIds,
    onChange,
    onAssign,
    onAssignSuccess,
    successMessage,
    errorMessage
  });

  const formatOptionLabel = useCallback(
    (option: Option, meta: { context: string }) =>
      renderOptionLabel(option, meta, select.auth?.id, compact),
    [select.auth?.id, compact]
  );

  return (
    <div
      role="presentation"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      className={inputClassName || "w-full"}
    >
      {label && (
        <label
          className={cx(
            "block text-sm font-medium text-content-1000 mb-1",
            labelClassName
          )}
        >
          {label}
        </label>
      )}
      <Select
        id={`assigned-to-select-${rowId || "default"}`}
        size={size}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        className={cx("w-full", inputClassName)}
        value={select.value ?? undefined}
        options={select.options}
        isDisabled={select.isLoading}
        placeholder="Unassigned"
        legacyErrorMargins={false}
        searchable={searchable}
        styles={compact ? compactSelectStyles : selectStyles}
        components={
          compact
            ? {
                DropdownIndicator: CompactDropdownIndicator,
                IndicatorSeparator: null
              }
            : undefined
        }
        onChange={select.handleChange}
        formatOptionLabel={formatOptionLabel}
      />
    </div>
  );
};
